import React, { useEffect, useId, useState } from "react";

/**
 * BdrDisclosure — the app's collapsible section row.
 *
 * The CSS lives in the global `index.css` (`.bdr-disclosure-*`, registry §26).
 * It replaces `header-buttons-collapse-sticky` + `PlusMinusCollapse` + the
 * `content-hide` / `content-show*` body classes, which 13 components had each
 * wired together by hand.
 *
 * It serves two jobs that look different but share one anatomy:
 *
 *   A — a section of a record that folds (the Client*Display components)
 *   B — a disclosure that reveals a tool (CreateClient, RecentBAGsDropdown…)
 *
 * The difference between them is which optional slots are filled, not the
 * structure. A tile grid could only ever serve A, which is why tiles are a
 * separate component rather than this one's replacement.
 *
 *   <BdrDisclosure
 *     open={!barCollapsed}
 *     onToggle={toggle}
 *     label="Invoices"
 *     count={invoices.length}
 *     status={{ tone: "danger", text: "1 overdue" }}
 *     action={<BdrButton size="sm" variant="action">+ New Invoice</BdrButton>}
 *   >
 *     …body…
 *   </BdrDisclosure>
 */

/*
 * Must match the `grid-template-rows` transition in index.css.
 *
 * The body animates to its TRUE height (`grid-template-rows: 0fr -> 1fr`), not
 * to a guessed `max-height`. The old classes ran `max-height: 0 -> 2200px`
 * linearly, so a 400px section finished changing about 18% in: expanding was a
 * snap followed by half a second of nothing, collapsing was half a second of
 * nothing followed by a snap. The scroll jump that snap caused is why the
 * clients page had to carry filler space at the bottom.
 *
 * `overflow` has to stay hidden for the whole slide or the body spills past its
 * animating row. Only once it settles can it scroll (capped) or overflow
 * visibly (uncapped — so a popover opened inside a section is not clipped).
 * Every one of the 13 call sites carried its own copy of this timer, with the
 * delay in the JSX and the duration it had to outlast in a different
 * stylesheet. Here they are adjacent, which is the point.
 */
const ANIM_MS = 350;
const SETTLE_MS = ANIM_MS + 60;

/*
 * Exported so anything else rendering the same `{ tone, text }` payload paints
 * it identically — the client console's tiles are the second consumer. Two
 * copies of this map is how one status meaning ends up as two different reds.
 * Note the vocabulary is `accent`, not `success`: that is what callers emit.
 */
export const TONE_CLASS = {
  danger: "bdr-badge--danger",
  warning: "bdr-badge--warning",
  accent: "bdr-badge--accent",
};

const BdrDisclosure = ({
  open,
  onToggle,
  /** the section name — the bar's accessible name when it is a string */
  label,
  /** optional MUI icon element. Rendered in a NEUTRAL tile: see the note below */
  icon,
  /**
   * `undefined` renders no badge at all. `0` is the EMPTY state — a muted dash,
   * a recessed bar, and `emptyText` where the status would sit.
   */
  count,
  /**
   * What this row says about itself. Either:
   *   a string / node — muted, no chip ("Last: Sep 20")
   *   { tone, text }  — a filled badge ("1 overdue")
   *
   * COLOUR IS RESERVED FOR STATUS. Red, amber and green appear on a row only
   * to report state — never as a per-section identity. A section whose icon is
   * permanently red has no way left to say that something is wrong with it.
   */
  status,
  emptyText = "None yet",
  /**
   * A control in the header, e.g. "+ New Invoice". It is a real sibling button,
   * NOT a child of the trigger — nesting a button inside a button is invalid
   * and the inner one will not reliably fire.
   */
  action,
  /**
   * "auto" (default) — the body flows at its natural height.
   * "cap"           — the body takes a max height and scrolls inside itself.
   */
  bodyScroll = "auto",
  /**
   * The header pins while its own body scrolls under it. It is therefore an
   * OPAQUE surface — a translucent lift cannot occlude what passes beneath it.
   */
  sticky = true,
  className = "",
  bodyClassName = "",
  /** for a caller that needs a scroll target or a stable hook */
  id,
  /*
   * Render the CONTENT ONLY — no header, no trigger, no collapse. For a
   * host that already provides the chrome and the choosing: the Image
   * Groups page is a tab page now, so the tab IS the disclosure and a
   * second bar inside it would be a control that opens what is already
   * open. `open` and `onToggle` are ignored in this mode.
   */
  bare = false,
  children,
}) => {
  const autoId = useId();
  const bodyId = `${id || "bdr-disclosure"}-${autoId}-body`;

  const [settled, setSettled] = useState(Boolean(open));

  useEffect(() => {
    if (!open) {
      setSettled(false);
      return undefined;
    }

    const timer = setTimeout(() => setSettled(true), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [open]);

  // `count === 0` is empty; `count === undefined` just means "this section does
  // not report a count", which is not the same thing.
  const isEmpty = count === 0;

  const rootClass = [
    "bdr-disclosure",
    open ? "is-open" : "is-closed",
    isEmpty ? "is-empty" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const statusNode = (() => {
    if (isEmpty) {
      return <span className="bdr-disclosure-empty">{emptyText}</span>;
    }
    if (status === undefined || status === null || status === "") return null;

    // A React element is also `typeof "object"`, so check for one first —
    // otherwise a passed-in node would be read as a { tone, text } config.
    if (
      !React.isValidElement(status) &&
      typeof status === "object" &&
      status.text != null
    ) {
      return (
        <span
          className={["bdr-badge", TONE_CLASS[status.tone] || ""]
            .filter(Boolean)
            .join(" ")}
        >
          {status.text}
        </span>
      );
    }

    return <span className="bdr-disclosure-status">{status}</span>;
  })();

  /* Nothing to animate and nothing to toggle — so none of the grid/clip
     machinery below runs, and `settled` never matters. */
  if (bare) {
    return (
      <div className={["bdr-disclosure-bare", className].filter(Boolean).join(" ")}>
        {children}
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <div
        className={["bdr-disclosure-header", sticky ? "is-sticky" : ""]
          .filter(Boolean)
          .join(" ")}
      >
        {/*
          The trigger is an EMPTY button stretched over the whole bar, sitting
          behind the content row. That is what lets the entire header be one
          click target while `action` stays a legal sibling button rather than a
          nested one. The row above it is `pointer-events: none` so hovers and
          clicks fall through; the action re-enables them for itself alone.
        */}
        <button
          type="button"
          className="bdr-disclosure-trigger"
          onClick={onToggle}
          aria-expanded={Boolean(open)}
          aria-controls={bodyId}
          aria-label={typeof label === "string" ? label : undefined}
        />

        <div className="bdr-disclosure-row">
          {icon && <span className="bdr-disclosure-icon">{icon}</span>}

          <span className="bdr-disclosure-label">{label}</span>

          {count !== undefined && (
            <span className="bdr-badge bdr-disclosure-count">
              {isEmpty ? "–" : count}
            </span>
          )}

          <span className="bdr-disclosure-gap" />

          {statusNode}

          {action && (
            <>
              <span className="bdr-disclosure-divider" aria-hidden="true" />
              <span className="bdr-disclosure-action">{action}</span>
            </>
          )}

          <svg
            className="bdr-disclosure-chevron"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M6 9l6 6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/*
        Two elements, not one: the outer is the animating grid row, the inner is
        what clips. `grid-template-rows: 0fr -> 1fr` only animates if the row's
        content can be squeezed, which needs `min-height: 0` + `overflow: hidden`
        on a child rather than on the grid itself.
      */}
      <div id={bodyId} className="bdr-disclosure-body">
        <div
          className={[
            "bdr-disclosure-body-inner",
            bodyScroll === "cap" ? "bdr-disclosure-body-inner--cap" : "",
            settled ? "is-settled" : "",
            bodyClassName,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default BdrDisclosure;
